import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { GST_RATES } from "../vouchers/entry";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { mastersApi } from "../lib/api";
import { useCompany } from "./reference";
import { groupTree, ledgerKind, NATURE } from "./groups";
import { CURRENCIES } from "../lib/currency";
import { billsDifference, billwiseOn, emptyDraft, fromLedger, GSTIN_TYPES, toPayload, type Draft } from "./ledgerDraft";
import { Combobox } from "../ui/Combobox";
import { formKeys } from "../ui/form";
import { errText } from "../ui/useLoad";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { fmtSigned } from "../lib/money";
import { fromGstin } from "../lib/india";
import { useShortcuts } from "../lib/useShortcuts";
import { PartyTdsDocs } from "../tds/TdsPages";
import { toUrl } from "../shell/routes";

const REG_TYPES: [string, string][] = [
  ["regular", "Regular"], ["composition", "Composition"], ["unregistered", "Unregistered"], ["consumer", "Consumer"],
  ["sez", "SEZ"], ["overseas", "Overseas"], ["deemed_export", "Deemed export"],
];

function Section({ title, children, note }: { title: string; children: ReactNode; note?: ReactNode }) {
  return <fieldset className="section-box"><legend>{title}</legend>{note && <p className="muted small">{note}</p>}{children}</fieldset>;
}

// One ledger: the details shown follow the group it goes under (party, bank, income / expense, duties & taxes).
// Enter moves to the next field; Ctrl+S saves; Esc leaves (asking first if something changed); Alt+D deletes.
export function LedgerForm() {
  const { company, ref } = useCompany();
  const { ledgerId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const isNew = !ledgerId || ledgerId === "new";
  const listPath = toUrl(`masters/ledgers`);

  const groups = ref?.groups ?? [];
  const [draft, setDraft] = useState<Draft | null>(null);
  const [orig, setOrig] = useState<string>("");
  const [used, setUsed] = useState(false);
  const [err, setErr] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    if (!ref) return;
    let gone = false;
    if (isNew) {
      const g = groups.find((x) => x.id === params.get("group"));
      const d = emptyDraft(g);
      setDraft(d); setOrig(JSON.stringify(d)); setUsed(false);
    } else {
      mastersApi.ledger(company.id, ledgerId!).then((l) => {
        if (gone) return;
        const d = fromLedger(l, groups.find((x) => x.id === l.group_id));
        setDraft(d); setOrig(JSON.stringify(d)); setUsed(l.used);
      }).catch((e) => !gone && setLoadErr(errText(e)));
    }
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ledgerId, company.id]);

  const group = groups.find((g) => g.id === draft?.group_id);
  const kind = ledgerKind(group);
  const india = ref?.company.country_pack === "in";
  const closed = ref?.opening_year.closed_reason ?? null;
  const canEdit = ref?.can_edit ?? false;
  const dirty = !!draft && JSON.stringify(draft) !== orig;

  const groupOptions = useMemo(() => groupTree(groups).map((r) => ({ value: r.group.id, label: r.group.name, hint: NATURE[r.group.nature] })), [groups]);
  const stateOptions = useMemo(() => (ref?.gst_states ?? []).map((s) => ({ value: s.code, label: `${s.code} ${s.name}` })), [ref]);
  const provisionOptions = (kinds: string[], nr: boolean) => (ref?.tds_provisions ?? [])
    .filter((p) => kinds.includes(p.kind) && (p.residence === "any" || p.residence === (nr ? "non_resident" : "resident")))
    .map((p) => ({ value: p.code, label: `${p.code} — ${p.nature}`, hint: p.kind.toUpperCase() }));

  const leave = () => { if (dirty && canEdit) setLeaving(true); else nav(listPath); };
  useShortcuts([{ keys: "Alt+D", run: () => { if (!isNew && canEdit && !used) setDeleting(true); } }]);

  if (loadErr) return <section className="page"><PageHead crumb="Accounting › Accounts" title="Account" /><ErrorNote text={loadErr} /></section>;
  if (!ref || !draft) return <section className="page"><PageHead crumb="Accounting › Accounts" title={isNew ? "New account" : "Account"} /><Loading /></section>;

  const set = (patch: Partial<Draft>) => { setDraft({ ...draft, ...patch }); setSaved(null); };
  const setIn = <K extends keyof Draft>(k: K, patch: Partial<Draft[K]>) => set({ [k]: { ...(draft[k] as object), ...patch } } as Partial<Draft>);

  const changeGroup = (id: string | null) => {
    const g = groups.find((x) => x.id === id);
    const k = ledgerKind(g);
    const was = ledgerKind(group);
    set({
      group_id: id,
      is_party: k.party ? true : was.party ? false : draft.is_party,
      party_kind: k.party ? (draft.party_kind && draft.is_party ? draft.party_kind : k.partyKind as Draft["party_kind"]) : was.party ? null : draft.party_kind,
      opening: draft.opening.amount ? draft.opening : { ...draft.opening, side: g?.nature === "L" || g?.nature === "I" ? "Cr" : "Dr" },
    });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const { payload, errors } = toPayload(draft, group, { india, openingsOpen: !closed });
    if (errors.length) { setErr(errors); return; }
    setBusy(true); setErr([]);
    try {
      const id = await mastersApi.saveLedger(company.id, payload);
      if (isNew) {
        // like Tally: stay on a fresh form under the same group for the next one
        const d = emptyDraft(group);
        setDraft(d); setOrig(JSON.stringify(d));
        setSaved(`Saved “${draft.name.trim()}”.`);
        setTimeout(() => document.getElementById("ledger-name")?.focus(), 0);
        void id;
      } else {
        nav(listPath);
      }
    } catch (x) { setErr([errText(x)]); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await mastersApi.deleteLedger(company.id, ledgerId!); nav(listPath); }
    catch (x) { setErr([errText(x)]); setDeleting(false); } finally { setBusy(false); }
  };

  const bw = billwiseOn(draft, group);
  const diff = billsDifference(draft);
  const nr = draft.tdsParty.residence === "non_resident";
  const gstinNeeded = GSTIN_TYPES.includes(draft.gstParty.reg_type);
  const ro = !canEdit;

  return (
    <section className="page narrow">
      <PageHead crumb="Accounting › Accounts" title={isNew ? "New account" : draft.name || "Account"} />
      <form className="ledger-form" onSubmit={save} noValidate
        onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); if (leaving) setLeaving(false); else leave(); } else formKeys(e); }}>
        <fieldset disabled={ro} className="plain">
          <div className="fields two">
            <label>Name<input id="ledger-name" autoFocus value={draft.name} onChange={(e) => set({ name: e.target.value })} required /></label>
            {ref?.company.account_codes && <label>Account code <span className="muted small">{isNew ? "(given from the group when left empty)" : ""}</span><input className="short" value={draft.number} placeholder={isNew ? "auto" : ""} onChange={(e) => set({ number: e.target.value })} /></label>}
            <label>Under<Combobox options={groupOptions} value={draft.group_id} onChange={changeGroup} placeholder="Choose a group" /></label>
          </div>
          <div className="fields inline">
            <label className="check"><input type="checkbox" checked={draft.is_party} disabled={kind.party}
              onChange={(e) => set({ is_party: e.target.checked, party_kind: e.target.checked ? draft.party_kind ?? "both" : null })} /> Party (customer or supplier)</label>
            {draft.is_party && (
              <label>Party is
                <select value={draft.party_kind ?? ""} onChange={(e) => set({ party_kind: e.target.value as Draft["party_kind"] })}>
                  <option value="customer">a customer</option><option value="supplier">a supplier</option><option value="both">both</option>
                </select>
              </label>
            )}
            {!kind.bank && <label>Kept in<select value={draft.currency} onChange={(e) => set({ currency: e.target.value })}>
              <option value="">{ref.company.base_currency} (the books' currency)</option>
              {CURRENCIES.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}</select></label>}
            {!isNew && <label className="check"><input type="checkbox" checked={!draft.active} onChange={(e) => set({ active: !e.target.checked })} /> Inactive</label>}
            {group && (group.nature === "I" || group.nature === "E") && <label className="check" title="Entries to this account are shared over cost centres (Accounting › Cost centres)">
              <input type="checkbox" checked={!!draft.cost_centres_on} onChange={(e) => set({ cost_centres_on: e.target.checked })} /> Track by cost centre</label>}
            {!draft.is_party && <label title="Each entry to this account names the employee or student it is for, so one account shows each person's balance">Kept by person
              <select value={draft.track_people ?? ""} onChange={(e) => set({ track_people: e.target.value as "" | "employee" | "student" })}>
                <option value="">No — one balance</option><option value="employee">Employee (each entry names one)</option>
                {company.purpose === "education" && <option value="student">Student (each entry names one)</option>}</select></label>}
          </div>

          <Section title={`Opening balance — ${ref.opening_year.label}`} note={closed ? `Can't be changed: ${closed}.` : undefined}>
            <div className="fields inline">
              <label>Amount<input inputMode="decimal" className="num" value={draft.opening.amount} disabled={!!closed}
                onChange={(e) => setIn("opening", { amount: e.target.value })} placeholder="0.00" /></label>
              <label>Dr / Cr<select value={draft.opening.side} disabled={!!closed} onChange={(e) => setIn("opening", { side: e.target.value as "Dr" | "Cr" })}>
                <option>Dr</option><option>Cr</option></select></label>
            </div>
          </Section>

          {(draft.is_party || draft.billwise.on) && (
            <Section title="Bill-wise">
              <div className="fields inline">
                <label className="check"><input type="checkbox" checked={bw} onChange={(e) => setIn("billwise", { on: e.target.checked })} /> Track bills (outstanding by invoice)</label>
                {bw && <label>Credit days<input inputMode="numeric" className="num short" value={draft.billwise.credit_days}
                  onChange={(e) => setIn("billwise", { credit_days: e.target.value })} placeholder="0" /></label>}
              </div>
              {bw && !closed && (
                <div className="bills">
                  <table className="grid-table compact">
                    <thead><tr><th>Bill ref.</th><th>Date</th><th>Due</th><th className="num">Amount</th><th>Dr/Cr</th><th></th></tr></thead>
                    <tbody>
                      {draft.bills.map((b, i) => {
                        const upd = (p: Partial<typeof b>) => set({ bills: draft.bills.map((x, j) => (j === i ? { ...x, ...p } : x)) });
                        return (
                          <tr key={i}>
                            <td><input value={b.ref_name} onChange={(e) => upd({ ref_name: e.target.value })} aria-label="Bill reference" /></td>
                            <td><input type="date" value={b.bill_date} max={ref.company.books_begin} onChange={(e) => upd({ bill_date: e.target.value })} aria-label="Bill date" /></td>
                            <td><input type="date" value={b.due_date} onChange={(e) => upd({ due_date: e.target.value })} aria-label="Due date" /></td>
                            <td><input inputMode="decimal" className="num" value={b.amount} onChange={(e) => upd({ amount: e.target.value })} aria-label="Bill amount" /></td>
                            <td><select value={b.side} onChange={(e) => upd({ side: e.target.value as "Dr" | "Cr" })} aria-label="Dr or Cr"><option>Dr</option><option>Cr</option></select></td>
                            <td><button type="button" className="link" tabIndex={-1} onClick={() => set({ bills: draft.bills.filter((_, j) => j !== i) })} aria-label="Remove bill">✕</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="row">
                    <button type="button" data-field onClick={() => {
                      set({ bills: [...draft.bills, { ref_name: "", bill_date: ref.company.books_begin, due_date: "", side: draft.opening.side, amount: "" }] });
                      setTimeout(() => { const ins = document.querySelectorAll<HTMLInputElement>(".bills tbody tr:last-child input"); ins[0]?.focus(); }, 0);
                    }}>Add opening bill</button>
                    {(draft.bills.length > 0 || draft.opening.amount) && (
                      <span className={`small ${diff === 0 ? "muted" : "warn"}`}>
                        {diff === 0 ? "Bills match the opening balance." : `Bills differ from the opening balance by ${fmtSigned(diff)}.`}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </Section>
          )}

          {kind.bank && (
            <Section title="Bank account">
              <div className="fields two">
                <label>Bank name<input value={draft.bank.bank_name} onChange={(e) => setIn("bank", { bank_name: e.target.value })} /></label>
                <label>Account number<input value={draft.bank.account_no} onChange={(e) => setIn("bank", { account_no: e.target.value })} /></label>
                <label>IFSC<input value={draft.bank.branch_code} onChange={(e) => setIn("bank", { branch_code: e.target.value.toUpperCase() })} /></label>
                <label>Currency<select value={draft.bank.currency} onChange={(e) => setIn("bank", { currency: e.target.value })}>
                  <option value={ref.company.base_currency}>{ref.company.base_currency}</option>
                  {CURRENCIES.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}</select></label>
              </div>
            </Section>
          )}

          {india && draft.is_party && (
            <Section title="GST">
              <div className="fields two">
                <label>Registration type<select value={draft.gstParty.reg_type}
                  onChange={(e) => setIn("gstParty", { reg_type: e.target.value, ...(e.target.value === "overseas" ? { state_code: "96", gstin: "" } : {}) })}>
                  {REG_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
                {gstinNeeded && <label>GSTIN<input value={draft.gstParty.gstin} maxLength={15} className="mono"
                  onChange={(e) => {
                    const g = e.target.value.toUpperCase();
                    const info = fromGstin(g);
                    setDraft({ ...draft, gstParty: { ...draft.gstParty, gstin: g, ...(info ? { state_code: info.state } : {}) },
                               tdsParty: info?.pan && !draft.tdsParty.pan ? { ...draft.tdsParty, pan: info.pan } : draft.tdsParty });
                    setSaved(null);
                  }} /></label>}
                <label>State<Combobox options={stateOptions} value={draft.gstParty.state_code || null} onChange={(v) => setIn("gstParty", { state_code: v ?? "" })} placeholder="Choose a state" /></label>
                <label>Legal name<input value={draft.gstParty.legal_name} onChange={(e) => setIn("gstParty", { legal_name: e.target.value })} /></label>
                <label>Address<input value={draft.gstParty.address} onChange={(e) => setIn("gstParty", { address: e.target.value })} /></label>
                <label>Place<input value={draft.gstParty.place} onChange={(e) => setIn("gstParty", { place: e.target.value })} /></label>
                {gstinNeeded && <label className="check" title="Amazon, Flipkart, Meesho, Swiggy, Zomato, ONDC network participants and the like">
                  <input type="checkbox" checked={!!draft.gstParty.eco} onChange={(e) => setIn("gstParty", { eco: e.target.checked })} /> E-commerce operator (sales can go through it)</label>}
                <label>PIN code<input inputMode="numeric" maxLength={6} className="short" value={draft.gstParty.pincode} onChange={(e) => setIn("gstParty", { pincode: e.target.value })} /></label>
              </div>
            </Section>
          )}

          {india && kind.pnl && (
            <Section title="GST" note="The usual treatment on invoices; an entry can change it.">
              <div className="fields two">
                <label>Taxability<select value={draft.gstLedger.taxability} onChange={(e) => setIn("gstLedger", { taxability: e.target.value })}>
                  <option value="taxable">Taxable</option><option value="exempt">Exempt</option><option value="nil_rated">Nil-rated</option><option value="non_gst">Non-GST</option></select></label>
                <label>HSN / SAC<input inputMode="numeric" maxLength={8} value={draft.gstLedger.hsn_sac} onChange={(e) => setIn("gstLedger", { hsn_sac: e.target.value })} /></label>
                {draft.gstLedger.taxability === "taxable" && <label>GST rate<select value={draft.gstLedger.rate === "" ? "" : String(Number(draft.gstLedger.rate))} onChange={(e) => setIn("gstLedger", { rate: e.target.value })}>
                  <option value="">Choose the rate</option>{GST_RATES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>}
                {group?.nature === "E" && <label>Input tax credit<select value={draft.gstLedger.itc} onChange={(e) => setIn("gstLedger", { itc: e.target.value })}>
                  <option value="eligible">Eligible</option><option value="ineligible">Ineligible (blocked)</option></select></label>}
              </div>
            </Section>
          )}

          {india && kind.duties && (
            <Section title="GST tax account">
              <label className="check"><input type="checkbox" checked={draft.gstTax.on} onChange={(e) => setIn("gstTax", { on: e.target.checked })} /> This account holds GST (CGST, SGST, IGST or cess)</label>
              {draft.gstTax.on && ((ref.gst_registrations ?? []).length === 0 ? (
                <p className="muted small">Add the company's GST registration first (Company › Settings, coming later).</p>
              ) : (
                <div className="fields two">
                  <label>Registration<select value={draft.gstTax.registration_id} onChange={(e) => setIn("gstTax", { registration_id: e.target.value })}>
                    <option value="">Choose…</option>{(ref.gst_registrations ?? []).map((r) => <option key={r.id} value={r.id}>{r.gstin}</option>)}</select></label>
                  <label>Tax<select value={draft.gstTax.head} onChange={(e) => setIn("gstTax", { head: e.target.value })}>
                    <option value="cgst">CGST</option><option value="sgst">SGST / UTGST</option><option value="igst">IGST</option><option value="cess">Cess</option></select></label>
                  <label>Direction<select value={draft.gstTax.direction} onChange={(e) => setIn("gstTax", { direction: e.target.value })}>
                    <option value="output">Output (on sales)</option><option value="input">Input (credit on purchases)</option></select></label>
                  <label className="check"><input type="checkbox" checked={draft.gstTax.rcm} onChange={(e) => setIn("gstTax", { rcm: e.target.checked })} /> Reverse charge</label>
                </div>
              ))}
            </Section>
          )}

          {india && draft.is_party && (
            <Section title="TDS / TCS" note="Income-tax Act, 2025. The PAN is filled from the GSTIN when there is one.">
              <div className="fields two">
                <label>PAN<input value={draft.tdsParty.pan} maxLength={10} className="mono" onChange={(e) => setIn("tdsParty", { pan: e.target.value.toUpperCase() })} /></label>
                <label>Residence<select value={draft.tdsParty.residence} onChange={(e) => setIn("tdsParty", { residence: e.target.value })}>
                  <option value="resident">Resident</option><option value="non_resident">Non-resident</option></select></label>
                <label className="wide">Usual provision<Combobox options={provisionOptions(["tds", "tcs"], nr)} value={draft.tdsParty.default_provision || null}
                  allowEmpty emptyLabel="None" onChange={(v) => setIn("tdsParty", { default_provision: v ?? "" })} /></label>
                <label className="check"><input type="checkbox" checked={draft.tdsParty.senior_citizen} onChange={(e) => setIn("tdsParty", { senior_citizen: e.target.checked })} /> Senior citizen</label>
                <label className="check"><input type="checkbox" checked={draft.tdsParty.transporter_declaration} onChange={(e) => setIn("tdsParty", { transporter_declaration: e.target.checked })} /> Transporter's declaration (393(4))</label>
              </div>
              {nr && (
                <div className="fields two">
                  <label>Country (ISO code)<input maxLength={2} className="short" value={draft.tdsParty.country} onChange={(e) => setIn("tdsParty", { country: e.target.value.toUpperCase() })} /></label>
                  <label>Tax ID in that country<input value={draft.tdsParty.foreign_tax_id} onChange={(e) => setIn("tdsParty", { foreign_tax_id: e.target.value })} /></label>
                  <label>Tax residency certificate valid to<input type="date" value={draft.tdsParty.trc_valid_to} onChange={(e) => setIn("tdsParty", { trc_valid_to: e.target.value })} /></label>
                  <label>Treaty rate %<input inputMode="decimal" className="num short" value={draft.tdsParty.treaty_rate} onChange={(e) => setIn("tdsParty", { treaty_rate: e.target.value })} /></label>
                  <label className="check"><input type="checkbox" checked={draft.tdsParty.form_10f} onChange={(e) => setIn("tdsParty", { form_10f: e.target.checked })} /> Form 10F received</label>
                </div>
              )}
              {!isNew && <PartyTdsDocs party={ledgerId!} provisions={ref.tds_provisions ?? []} canEdit={canEdit} />}
            </Section>
          )}

          {india && kind.pnl && (
            <Section title="TDS / TCS" note="The provision that usually applies to entries on this account; an entry can change it.">
              <Combobox options={provisionOptions(group?.nature === "E" ? ["tds"] : ["tcs", "tds"], false)} value={draft.tdsLedger.provision_code || null}
                allowEmpty emptyLabel="None" onChange={(v) => setIn("tdsLedger", { provision_code: v ?? "" })} />
            </Section>
          )}
        </fieldset>

        {err.length > 0 && <ul className="msg err errors" role="alert">{err.map((x) => <li key={x}>{x}</li>)}</ul>}
        {saved && <p className="msg ok" role="status">{saved} Enter the next one, or press Esc to go back.</p>}
        {leaving ? (
          <div className="confirm" role="alert">You have unsaved changes.
            <button type="button" className="danger" autoFocus onClick={() => nav(listPath)}>Discard</button>
            <button type="button" onClick={() => setLeaving(false)}>Keep editing</button></div>
        ) : deleting ? (
          <div className="confirm" role="alert">Delete “{draft.name}”?
            <button type="button" className="danger" autoFocus onClick={remove} disabled={busy}>Delete</button>
            <button type="button" onClick={() => setDeleting(false)}>Keep</button></div>
        ) : (
          <div className="row form-actions">
            {canEdit && <button className="primary" disabled={busy}>{busy ? "Saving…" : "Save"} <kbd>Ctrl+S</kbd></button>}
            {canEdit && !isNew && (used
              ? <span className="muted small">Has entries, so it can't be deleted — tick Inactive instead.</span>
              : <button type="button" className="link danger-link" onClick={() => setDeleting(true)}>Delete <kbd>Alt+D</kbd></button>)}
            <button type="button" className="link" onClick={leave}>{canEdit ? "Cancel" : "Back"} <kbd>Esc</kbd></button>
          </div>
        )}
      </form>
    </section>
  );
}
